import React, {useState} from 'react';
import {getStoreCardImagePresentation, getStoreCardImageSourceKey, getStoreCardImageView, recordStoreCardImageFailure, storeCardPlaceholderSrc, type StoreProductCardImageInput} from './store-card-image-recovery';

export function StoreProductCardImage(props: StoreProductCardImageInput) {
    const sourceKey = getStoreCardImageSourceKey(props);
    return (
        <StoreProductCardImageAttempts
            key={sourceKey}
            {...props}
        />
    );
}

export function StoreProductCardImageAttempts({initialFailedUrls = [], ...input}: StoreProductCardImageInput & {initialFailedUrls?: readonly string[]}) {
    const [failedUrls, setFailedUrls] = useState<readonly string[]>(initialFailedUrls);
    const view = getStoreCardImageView(input, failedUrls);
    const presentation = getStoreCardImagePresentation(input);
    const alt = typeof input.alt === 'string' ? input.alt : typeof input.name === 'string' ? input.name : '';

    const handleError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (view.type === 'placeholder') return;
        const stageUrl = view.type === 'preferred' ? view.webpSrc : view.src;
        const originalSrc = view.type === 'preferred' ? view.originalSrc : '';
        // Read this before setState. React clears currentTarget after the handler returns,
        // and the updater runs after that, so reading it inside the updater throws.
        const currentSrc = event.currentTarget?.currentSrc ?? '';

        setFailedUrls(prev =>
            recordStoreCardImageFailure(prev, {
                stage: view.type,
                stageUrl,
                currentSrc,
                originalSrc,
            }),
        );
    };

    if (view.type === 'placeholder') {
        return (
            <img
                id={`prod-img-${input.productId}`}
                src={storeCardPlaceholderSrc(presentation.width, presentation.height)}
                alt={alt}
                width={presentation.width}
                height={presentation.height}
                className={presentation.className}
                style={presentation.style}
                loading="lazy"
            />
        );
    }

    if (view.type === 'preferred') {
        return (
            <picture id={`prod-img-${input.productId}`}>
                <source
                    type="image/webp"
                    srcSet={view.webpSrc}
                />
                <img
                    src={view.originalSrc}
                    alt={alt}
                    width={presentation.width}
                    height={presentation.height}
                    className={presentation.className}
                    style={presentation.style}
                    loading="lazy"
                    onError={handleError}
                />
            </picture>
        );
    }

    return (
        <img
            id={`prod-img-${input.productId}`}
            key={view.src}
            src={view.src}
            alt={alt}
            width={presentation.width}
            height={presentation.height}
            className={presentation.className}
            style={presentation.style}
            loading="lazy"
            onError={handleError}
        />
    );
}
